import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core'
import { HttpParams } from '@angular/common/http'
import { EMPTY, concatMap, expand, finalize, forkJoin, map, of, switchMap, toArray } from 'rxjs'
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import { DemandIntentDocument } from '@models/demand/demand-intent-document.model'
import { SupplierEnrollmentNoticeSearch } from '@models/supplier/notice/supplier-enrollment-notice.model'
import { SupplierEnrollmentNoticeService } from '@services/supplier-enrollment-notice.service'
import { SupplierEnrollmentNoticeMessageService, SupplierEnrollmentNoticeMessageItem } from '@services/supplier-enrollment-notice-message.service'
import { AttachmentService } from '@services/attachment.service'
import { groupSessionTimelineByDate, eventSourceIcon, SessionTimelineGroup } from '@utils/session-timeline.util'

export interface SessionEventTimelineItem extends SupplierEnrollmentNoticeMessageItem {
	enrollmentId: string
	supplierName: string
	supplierDocument: string
	approved: boolean
	rejected: boolean
}

export interface SessionEventParticipantSummary {
	id: string
	name: string
	document: string
	statusLabel: string
	approved: boolean
	rejected: boolean
}

@Component({
	selector: 'app-session-enrollment-events',
	templateUrl: './session-enrollment-events.component.html',
	standalone: false
})
export class SessionEnrollmentEventsComponent implements OnChanges {
	@ViewChild('modalAttachment') modalAttachment: NgbModalRef

	@Input() demand!: DemandIntentDocument

	events: SessionEventTimelineItem[] = []
	participants: SessionEventParticipantSummary[] = []
	selectedParticipantId: string | null = null
	eventsLoading = false
	eventsLoaded = false
	attachmentUrl: any
	attachmentLoading = false

	protected readonly eventSourceIcon = eventSourceIcon

	constructor(
		private modal: NgbModal,
		private attachmentService: AttachmentService,
		private supplierEnrollmentNoticeService: SupplierEnrollmentNoticeService,
		private supplierEnrollmentNoticeMessageService: SupplierEnrollmentNoticeMessageService
	) {}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['demand'] && this.demand?.noticeSession?.id && !this.eventsLoaded && !this.eventsLoading) {
			this.fetchSessionEvents()
		}
	}

	fetchSessionEvents() {
		const sessionId = this.demand?.noticeSession?.id

		if (!sessionId) {
			return
		}

		this.eventsLoading = true
		this.events = []
		this.participants = []
		this.selectedParticipantId = null

		let page = 0
		const params = new HttpParams().set('size', 30).set('page', page)
		const query = new SupplierEnrollmentNoticeSearch()

		const source = this.supplierEnrollmentNoticeService.searchSession(sessionId, params, query).pipe(
			expand(response => {
				if (response.last) return EMPTY
				return this.supplierEnrollmentNoticeService.searchSession(sessionId, params.set('page', ++page), query)
			}),
			concatMap(response => response.content),
			toArray(),
			switchMap(enrollments => {
				if (enrollments.length === 0) {
					return of({ enrollments, items: [] as SessionEventTimelineItem[] })
				}

				const sources = enrollments.map(enrollment =>
					this.supplierEnrollmentNoticeMessageService.list(enrollment.id).pipe(
						map(items =>
							items.map(
								item =>
									({
										...item,
										enrollmentId: enrollment.id,
										supplierName: enrollment.supplier?.name,
										supplierDocument: enrollment.supplier?.document,
										approved: enrollment.statusApproved,
										rejected: enrollment.statusRejected
									}) as SessionEventTimelineItem
							)
						)
					)
				)

				return forkJoin(sources).pipe(map(results => ({ enrollments, items: results.flat() })))
			})
		)

		source.pipe(finalize(() => (this.eventsLoading = false))).subscribe({
			next: ({ enrollments, items }) => {
				this.participants = enrollments.map(enrollment => ({
					id: enrollment.id,
					name: enrollment.supplier?.name,
					document: enrollment.supplier?.document,
					statusLabel: enrollment.statusLabel,
					approved: enrollment.statusApproved,
					rejected: enrollment.statusRejected
				}))
				this.events = items.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime())
				this.eventsLoaded = true
			},
			error: () => {
				this.events = []
				this.participants = []
			}
		})
	}

	toggleParticipantFilter(participantId: string) {
		this.selectedParticipantId = this.selectedParticipantId === participantId ? null : participantId
	}

	get filteredEventGroups(): SessionTimelineGroup<SessionEventTimelineItem>[] {
		const items = this.selectedParticipantId ? this.events.filter(it => it.enrollmentId === this.selectedParticipantId) : this.events

		return groupSessionTimelineByDate(items)
	}

	get qualifiedParticipants(): SessionEventParticipantSummary[] {
		return this.participants.filter(it => it.approved)
	}

	get rejectedParticipants(): SessionEventParticipantSummary[] {
		return this.participants.filter(it => it.rejected)
	}

	eventAttachmentDownload(event: SessionEventTimelineItem, resourceId: string) {
		if (!event.hasAttachment) {
			return
		}

		event.submitted = true
		this.attachmentLoading = true

		this.attachmentService
			.downloadHandleBlob(resourceId)
			.pipe(
				finalize(() => {
					event.submitted = false
					this.attachmentLoading = false
				})
			)
			.subscribe(response => {
				this.attachmentUrl = response
				this.modal.open(this.modalAttachment, { fullscreen: true })
			})
	}
}
